import { Link, useSearchParams } from 'react-router-dom';

import { EVENTS } from '@constants/telemetryEvents';
import { VISIT_PATH } from '@constants/publicSearch';
import { isPublicSiteViewSearch, withPublicSiteView } from '@constants/publicSiteView';
import { Button } from '@components/common/Button';
import { Container } from '@components/common/Container';
import { Card } from '@components/common/Card';
import { Icon } from '@components/Icon';
import { useTelemetry } from '@hooks/useTelemetry';
import { useRegistrationVisibility } from '@hooks/useRegistrationVisibility';
import { photos } from '@assets/photos';

const HERO_SUBHEADLINE =
  "Bethlehem Homeschool Group provides Christian-led education and support for homeschooling families in Knightdale, NC. Founded in 2017, we offer academic excellence, biblical worldview integration, and meaningful community.";

const HERO_CARD_DESCRIPTION =
  "BHG is a homeschool group that meets at Bethlehem Baptist Church in Knightdale, NC on Thursdays. We offer academic and enrichment classes for preschool to high school students, taught by BHG Board-approved tutors.";

export interface HeroSectionProps {
  /** Formatted first day e.g. "Thursday, January 8th" (from activeSemester.startDateFormatted) */
  firstDayLabel?: string;
  /** Semester label e.g. "Spring 2026" (from activeSemester.label) */
  semesterLabel?: string;
  /** True while active semester is being fetched — show skeleton instead of stale defaults. */
  semesterLoading?: boolean;
}

const heroPhoto = photos.teacherPicnic;

export function HeroSection({
  firstDayLabel,
  semesterLabel,
  semesterLoading = false,
}: HeroSectionProps) {
  const { showRegister, registrationPending } = useRegistrationVisibility();
  const { track } = useTelemetry();
  const [searchParams] = useSearchParams();
  const viewingPublicSite = isPublicSiteViewSearch(searchParams.toString());
  const scrollToAbout = () => {
    track(EVENTS.LANDING_HERO_LEARN_MORE);
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
  };

  const showSemesterBlock = semesterLoading || (firstDayLabel != null && semesterLabel != null);
  const showNoSemesterMessage = !semesterLoading && (firstDayLabel == null || semesterLabel == null);

  return (
    <section className="bhg-landing-hero">
      <div className="bhg-landing-hero__background">
        <img
          className="bhg-landing-hero__background-img"
          src={heroPhoto.src}
          srcSet={heroPhoto.srcSet}
          sizes={heroPhoto.heroSizes}
          alt=""
          fetchpriority="high"
          decoding="async"
        />
        <div className="bhg-landing-hero__overlay" />
      </div>
      <Container size="lg">
        <div className="bhg-landing-hero__content">
          <div className="bhg-landing-hero__text">
            <h1 className="bhg-landing-hero__title">
              A community dedicated to enriching your homeschool journey
            </h1>
            <p className="bhg-landing-hero__description">{HERO_SUBHEADLINE}</p>
            <div className="bhg-landing-hero__actions">
              <Button variant="primary" size="lg" onClick={scrollToAbout}>
                Learn More
              </Button>
              <Link
                to={withPublicSiteView(VISIT_PATH, viewingPublicSite)}
                state={{ visitSource: 'hero' }}
                className="bhg-button bhg-button--outline bhg-button--lg"
                onClick={() => track(EVENTS.LANDING_HERO_SCHEDULE_VISIT)}
              >
                <span className="bhg-button__content">Schedule a Visit</span>
              </Link>
            </div>
          </div>
          <div className="bhg-landing-hero__card">
            <Card elevation={4} className="bhg-landing-hero__card-inner" style={{ height: '100%' }}>
              <div className="bhg-landing-hero__card-content">
                <h2 className="bhg-landing-hero__card-title">Welcome to BHG!</h2>
                <p className="bhg-landing-hero__card-text">
                  {HERO_CARD_DESCRIPTION}
                </p>
                {showSemesterBlock ? (
                  <div
                    className="bhg-landing-hero__card-highlight"
                    aria-busy={semesterLoading || undefined}
                  >
                    <Icon
                      type="star"
                      style="filled"
                      colorVariant="orange"
                      size="md"
                      aria-label="Star"
                    />
                    <div className="bhg-landing-hero__card-highlight-text">
                      {semesterLoading ? (
                        <>
                          <div
                            className="bhg-landing-hero__skeleton-line bhg-landing-hero__skeleton-line--label"
                            aria-hidden
                          />
                          <div
                            className="bhg-landing-hero__skeleton-line bhg-landing-hero__skeleton-line--date"
                            aria-hidden
                          />
                        </>
                      ) : (
                        <>
                          <p className="bhg-landing-hero__card-label">
                            {semesterLabel} Classes Begin
                          </p>
                          <p className="bhg-landing-hero__card-date">
                            {firstDayLabel}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                ) : null}
                {showNoSemesterMessage ? (
                  <div className="bhg-landing-hero__card-no-semester">
                    <Icon
                      type="calendar"
                      style="outlined"
                      colorVariant="orange"
                      size="lg"
                      aria-label=""
                    />
                    <p role="status">
                      Our next semester schedule will be posted soon.
                    </p>
                  </div>
                ) : null}
                {registrationPending ? (
                  <div
                    className="bhg-landing-hero__register-skeleton bhg-button bhg-button--secondary bhg-button--lg"
                    aria-busy="true"
                    aria-label="Loading registration status"
                  />
                ) : null}
                {showRegister ? (
                  <Link
                    to="/register"
                    className="bhg-button bhg-button--secondary bhg-button--lg"
                    style={{
                      width: '100%',
                      display: 'block',
                      textAlign: 'center',
                      boxSizing: 'border-box',
                      textDecoration: 'none',
                    }}
                  >
                    Register Now
                  </Link>
                ) : null}
              </div>
            </Card>
          </div>
        </div>
      </Container>
    </section>
  );
}
